import React, { useMemo } from 'react';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { Home, ClipboardList, FileText, UserCheck, LogOut, RotateCcw, Activity, Layers3, Target, Calendar, BarChart3, HeartHandshake, Brain, MapPin, Zap, MessageCircle, Users, Star, MonitorSpeaker } from 'lucide-react';
import { useToolsData } from '@/hooks/useToolsData';

interface BreadcrumbItem {
  label: string;
  href?: string;
  icon?: React.ReactNode;
}

interface AppHeaderProps {
  title: string;
  subtitle?: string;
  breadcrumbs?: BreadcrumbItem[];
  children?: React.ReactNode;
  className?: string;
  size?: 'compact' | 'default' | 'large';
  showChips?: boolean; // show chip carousel of tools
  showLogo?: boolean; // show JiGSO logo before title
}

export const AppHeader: React.FC<AppHeaderProps> = ({
  title,
  subtitle,
  breadcrumbs = [],
  children,
  className = "",
  size = "default",
  showChips = true,
  showLogo = false,
}) => {
  const defaultBreadcrumbs: BreadcrumbItem[] = [
    { label: "Hub", href: "/", icon: <Home className="h-4 w-4" /> },
    ...breadcrumbs
  ];

  // Pull tool names and icons to showcase in the chip carousel
  const { data: tools = [] } = useToolsData();

  const iconMap: Record<string, any> = {
    ClipboardList, FileText, UserCheck, LogOut, RotateCcw, Activity, Layers3, Target,
    Calendar, BarChart3, HeartHandshake, Brain, MapPin, Zap, MessageCircle, Users, Star, MonitorSpeaker
  };

  const chips = useMemo(() =>
    (tools || [])
      .filter((t: any) => t?.is_active)
      .map((t: any) => ({ id: t.id, name: t.name, Icon: iconMap[t.icon] || ClipboardList }))
  , [tools]);

  return (
    <div className={`relative bg-gradient-subtle text-foreground overflow-hidden ${className}`}>
      {/* Decorative background elements */}
      <div className="absolute inset-0">
        <div className="absolute -top-24 -right-24 w-[28rem] h-[28rem] rounded-full bg-gradient-to-br from-primary/15 to-accent/15 blur-3xl"></div>
        <div className="absolute -bottom-24 -left-24 w-[22rem] h-[22rem] rounded-full bg-gradient-to-tr from-accent/10 to-primary/10 blur-2xl"></div>
        {/* Partially visible accent circles */}
        <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full ring-2 ring-primary/15"></div>
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full ring-2 ring-accent/15"></div>
      </div>

      {/* Soft overlay to add subtle color depth */}
      <div className="absolute inset-0 bg-gradient-header pointer-events-none"></div>

      <div className={`relative z-10 container mx-auto px-4 ${size === 'large' ? 'py-20 pb-28' : size === 'compact' ? 'py-10 pb-12' : 'py-14 pb-20'}`}>
        <div className="max-w-4xl mx-auto">
          {/* Breadcrumb */}
          {defaultBreadcrumbs.length > 1 && (
            <Breadcrumb className="mb-8">
              <BreadcrumbList>
                {defaultBreadcrumbs.map((item, index) => (
                  <React.Fragment key={`breadcrumb-${index}`}>
                    <BreadcrumbItem>
                      {item.href ? (
                        <BreadcrumbLink 
                          href={item.href} 
                          className="flex items-center gap-1 text-muted-foreground hover:text-foreground"
                        >
                          {item.icon}
                          {item.label}
                        </BreadcrumbLink>
                      ) : (
                        <BreadcrumbPage className="text-foreground flex items-center gap-1">
                          {item.icon}
                          {item.label}
                        </BreadcrumbPage>
                      )}
                    </BreadcrumbItem>
                    {index < defaultBreadcrumbs.length - 1 && (
                      <BreadcrumbSeparator className="text-muted-foreground" />
                    )}
                  </React.Fragment>
                ))}
              </BreadcrumbList>
            </Breadcrumb>
          )}

          {/* Header Content */}
          <div className="text-center">
            <div className="flex items-center justify-center gap-4 mb-4">
              {showLogo && (
                <img 
                  src="/lovable-uploads/JiGSO_analytics.png" 
                  alt="JiGSO Logo" 
                  className="w-16 h-16 md:w-20 md:h-20 animate-logo-rotate opacity-90 hover:opacity-100 transition-opacity duration-300"
                />
              )}
              <h1 className="text-4xl md:text-5xl font-bold text-foreground">
                {title}
              </h1>
            </div>
            {subtitle && (
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8 leading-relaxed">
                {subtitle}
              </p>
            )}
            {children}
            {showChips && chips.length > 0 && (
              <div className="mt-6">
                <div className="chip-marquee">
                  <div className="chip-track">
                    {[...chips, ...chips].map((chip, idx) => {
                      const IconComp = chip.Icon;
                      return (
                        <span key={`chip-${idx}`} className="chip inline-flex items-center gap-2">
                          <IconComp className="h-4 w-4 text-primary" />
                          {chip.name}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Powered by Jigso chip */}
          <div className="flex justify-center mt-8">
            <a 
              href="https://jigso.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-secondary rounded-full text-foreground text-sm font-medium hover:bg-primary/10 hover:text-primary transition-colors"
            >
              <img 
                src="/lovable-uploads/822c9934-bc7f-4f51-afef-cd4280c5d405.png" 
                alt="Jigso logo" 
                className="w-4 h-4 rounded-sm"
              />
              Powered by jigso.com
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};